import axios from "axios";
import { QueryClient, useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import store2 from "store2";
import { api, baseUrl, financeBaseUrl, FINANCE_USERS } from "./config";
import { Credentials, ImpersonationCredentials, UserFromAPI } from "../types/auth.types";
import { useNavigate } from "react-router-dom";
import { getRecords } from "./useRecords";
import { CollectionAndFieldsPermission, getCollections } from "./useCollections";
import { ROLES } from "../utils/constants";
import { clearStoredSession, QUERY_CACHE_STORAGE_KEY } from "./session";
import { getLoginErrorCode, toFinanceLoginError } from "./loginErrors";

type AuthTokens = {
    access_token: string;
    refresh_token: string;
    expires: number;
    expires_at?: number;
};

type ImpersonationTokens = {
    access_token: string;
    expires: number;
};

type Impersonation = {
    caller_email: string;
};

type StoredAuth = {
    access_token: string;
    refresh_token?: string;
    expires?: number;
    expires_at: number;
    impersonation?: Impersonation;
};

type ReplaceStoredSessionParameters = {
    operationsAuth: StoredAuth;
    financeAuth: StoredAuth | null;
    queryClient: QueryClient;
};

const readTokenUserId = (accessToken: string | undefined) => {
    try {
        const tokenUserId: string | null = decodeJWT(accessToken ?? "").id ?? null;
        return tokenUserId;
    } catch (error) {
        return null;
    }
};

const isSameUserSession = (previousAuth: StoredAuth | null, nextAuth: StoredAuth) => {
    const previousUserId = readTokenUserId(previousAuth?.access_token);
    const involvesImpersonation = Boolean(previousAuth?.impersonation || nextAuth.impersonation);
    const isSameUser = previousUserId !== null && previousUserId === readTokenUserId(nextAuth.access_token) && !involvesImpersonation;
    return isSameUser;
};

export const replaceStoredSession = ({ operationsAuth, financeAuth, queryClient }: ReplaceStoredSessionParameters) => {
    queryClient.removeQueries();

    if (isSameUserSession(store2.get("auth"), operationsAuth)) {
        store2.remove(QUERY_CACHE_STORAGE_KEY);
    } else {
        clearStoredSession();
    }

    store2.set("auth", operationsAuth);
    if (financeAuth) {
        store2.set("finance_auth", financeAuth);
    } else {
        store2.remove("finance_auth");
    }
};

const getStartPath = (user: UserFromAPI | undefined) => {
    switch (user?.role?.id) {
        case ROLES.virtual_assistant:
        case ROLES.accounting:
            return "/bank_transactions";
        case ROLES.tech_people:
            return `/staff-work-logs/${user?.id}`;
        case ROLES.human_resources:
            return "/candidates";
        default:
            return "/sessions";
    }
};

const loginToFinance = async (credentials: Credentials) => {
    try {
        const financeTokens = await login(credentials, financeBaseUrl);
        return financeTokens;
    } catch (financeError) {
        throw toFinanceLoginError(financeError);
    }
};

const loginToBothInstances = async (credentials: Credentials) => {
    const operationsTokens = await login(credentials, baseUrl);
    const financeTokens = FINANCE_USERS.includes(credentials.email.toLowerCase()) ? await loginToFinance(credentials) : null;
    const loginTokens = { operationsTokens, financeTokens };
    return loginTokens;
};

export const useLogin = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { mutate, error, isError } = useMutation<Awaited<ReturnType<typeof loginToBothInstances>>, unknown, Credentials>(
        (credentials) => loginToBothInstances(credentials),
        {
            onSuccess: async ({ operationsTokens, financeTokens }) => {
                const operationsAuth = {
                    refresh_token: operationsTokens.refresh_token,
                    access_token: operationsTokens.access_token,
                    expires_at: Date.now() + operationsTokens.expires * 1000,
                };
                const financeAuth = financeTokens ? { ...financeTokens, expires_at: Date.now() + financeTokens.expires * 1000 } : null;
                replaceStoredSession({ operationsAuth, financeAuth, queryClient });

                await queryClient.prefetchQuery({
                    queryKey: ["user"],
                    queryFn: async () => await getUser(),
                });

                const userData = queryClient.getQueryData<UserFromAPI>(["user"]);
                navigate(getStartPath(userData));
            },
        }
    );
    return { mutate, error, isError };
};

const requestImpersonation = async (impersonationCredentials: ImpersonationCredentials, apiBaseUrl: string) => {
    const response = await axios({
        method: "POST",
        url: `${apiBaseUrl}/impersonate`,
        data: impersonationCredentials,
    });
    const impersonationTokens: ImpersonationTokens = response.data.data;
    return impersonationTokens;
};

const requestFinanceImpersonation = async (impersonationCredentials: ImpersonationCredentials) => {
    try {
        const financeTokens = await requestImpersonation(impersonationCredentials, financeBaseUrl);
        return financeTokens;
    } catch (financeError) {
        if (getLoginErrorCode(financeError) === "TARGET_NOT_FOUND") {
            return null;
        }
        throw toFinanceLoginError(financeError);
    }
};

const getUserWithAccessToken = async (accessToken: string) => {
    const response = await axios({
        method: "GET",
        url: `${baseUrl}/users/me?fields=*.*`,
        headers: { Authorization: `Bearer ${accessToken}` },
    });
    const user: UserFromAPI = response.data.data;
    return user;
};

const impersonateOnBothInstances = async (impersonationCredentials: ImpersonationCredentials) => {
    const operationsTokens = await requestImpersonation(impersonationCredentials, baseUrl);
    const financeTokens = FINANCE_USERS.includes(impersonationCredentials.email.toLowerCase())
        ? await requestFinanceImpersonation(impersonationCredentials)
        : null;
    const impersonatedUser = await getUserWithAccessToken(operationsTokens.access_token);
    const impersonationResult = { operationsTokens, financeTokens, impersonatedUser };
    return impersonationResult;
};

const toImpersonationAuth = (impersonationTokens: ImpersonationTokens, impersonation: Impersonation) => {
    const impersonationAuth: StoredAuth = {
        access_token: impersonationTokens.access_token,
        expires_at: Date.now() + impersonationTokens.expires,
        impersonation,
    };
    return impersonationAuth;
};

export const useImpersonateLogin = () => {
    const navigate = useNavigate();
    const queryClient = useQueryClient();

    const { mutate, error, isError } = useMutation<
        Awaited<ReturnType<typeof impersonateOnBothInstances>>,
        unknown,
        ImpersonationCredentials
    >((impersonationCredentials) => impersonateOnBothInstances(impersonationCredentials), {
        onSuccess: ({ operationsTokens, financeTokens, impersonatedUser }, impersonationCredentials) => {
            const impersonation = { caller_email: impersonationCredentials.email };
            const operationsAuth = toImpersonationAuth(operationsTokens, impersonation);
            const financeAuth = financeTokens ? toImpersonationAuth(financeTokens, impersonation) : null;
            replaceStoredSession({ operationsAuth, financeAuth, queryClient });

            queryClient.setQueryData(["user"], impersonatedUser);
            navigate(getStartPath(impersonatedUser));
        },
    });
    return { mutate, error, isError };
};

export const useUser = () => {
    const auth = store2.get("auth");
    const navigate = useNavigate();

    return useQuery({
        queryKey: ["user"],
        queryFn: async () => {
            if (!auth) {
                navigate("/login");
                return;
            }
            return getUser();
        },
        staleTime: Infinity,
    });
};

export const useUserNotificationPreferences = () => {
    const queryClient = useQueryClient();
    return useQuery({
        queryKey: ["notifications_user_config"],
        queryFn: async () => {

            const collectionsData = queryClient.getQueryData(["collections"]) as CollectionAndFieldsPermission
			let collections = collectionsData?.collections
            if (!collections) collections = await getCollections()
            const collection = collections.find((collection: any) => collection.collection === "notifications_user_config");

            if (!collection) return null;
            const preferences = await getRecords("notifications_user_config", "*.*");
            return preferences;
        },
        staleTime: Infinity,
        refetchOnWindowFocus: false,
    });
};

const login = async (credentials: Credentials, apiBaseUrl: string): Promise<AuthTokens> => {
    const response = await axios({
        method: "POST",
        url: `${apiBaseUrl}/auth/login`,
        data: {
            email: credentials.email,
            password: credentials.password,
        },
    });

    return {
        access_token: response.data.data.access_token,
        refresh_token: response.data.data.refresh_token,
        expires: response.data.data.expires,
    };
};

export async function refreshToken(refresh_token: AuthTokens["refresh_token"], apiBaseUrl: string) {
    const response = await axios({
        method: "POST",
        url: `${apiBaseUrl}/auth/refresh`,
        data: {
            refresh_token: refresh_token,
        },
    });

    return response.data.data;
}

export async function getUser() {
    const response = await api({
        method: "GET",
        url: `/users/me?fields=*.*`,
    });

    return response.data.data as UserFromAPI;
}

export function decodeJWT(token: string) {
    // Split the token into three parts
    const payload = token.split(".")[1];

    // Base64 decode the payload
    const decodedPayload = JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")));

    // Return the decoded payload
    return decodedPayload;
}
